// ===================================
// TRENCH QUEST - Game Clock System
// Real-time clock that persists offline
// Accelerated time: 1 real minute = 1 game hour (configurable)
// ===================================

export interface GameTime {
  // Game world time
  gameDay: number
  gameHour: number // 0-23
  gameMinute: number // 0-59

  // Real world tracking
  startTimestamp: number // When game started
  lastUpdateTimestamp: number // Last time we updated
  totalGameHours: number // Total hours passed in game

  // Time scaling
  timeScale: number // Real minutes per game hour (1 = 1 min = 1 game hour)
}

export interface ClockConfig {
  startDay: number
  startHour: number
  timeScale: number // Real minutes per game hour
}

/**
 * Time scale presets for different play styles
 */
export const TIME_SCALES = {
  relaxed: 2,     // 2 real minutes = 1 game hour (slower, more time to think)
  normal: 1,      // 1 real minute = 1 game hour (default)
  fast: 0.5,      // 30 real seconds = 1 game hour
  turbo: 0.25,    // 15 real seconds = 1 game hour (intense)
} as const

export type TimeScalePreset = keyof typeof TIME_SCALES

// Create initial game time
export const createGameTime = (config: Partial<ClockConfig> = {}): GameTime => {
  const now = Date.now()
  return {
    gameDay: config.startDay || 1,
    gameHour: config.startHour || 9, // Start at 9 AM
    gameMinute: 0,
    startTimestamp: now,
    lastUpdateTimestamp: now,
    totalGameHours: 0,
    timeScale: config.timeScale || 1,
  }
}

/**
 * Calculate game time from real milliseconds elapsed
 */
export const calculateGameTimeFromMs = (
  realMsElapsed: number,
  timeScale: number
): number => {
  // timeScale = real minutes per game hour
  const realMinutes = realMsElapsed / 60000
  return realMinutes / timeScale // Returns game hours
}

// Calculate time passed since last update
export const calculateTimePassed = (gameTime: GameTime): {
  realHoursPassed: number
  gameHoursPassed: number
  realMinutesPassed: number
  newGameTime: GameTime
} => {
  const now = Date.now()
  const realMsPassed = now - gameTime.lastUpdateTimestamp
  const realHoursPassed = realMsPassed / (1000 * 60 * 60)
  const realMinutesPassed = realMsPassed / 60000

  // Apply time scaling: timeScale = real minutes per game hour
  // So if timeScale=1, 1 real minute = 1 game hour
  const gameHoursPassed = realMinutesPassed / gameTime.timeScale

  // Calculate new game time
  const totalMinutes = gameTime.gameMinute + (gameHoursPassed * 60)
  const totalHours = gameTime.gameHour + Math.floor(totalMinutes / 60)
  const newMinute = Math.floor(totalMinutes) % 60
  const additionalDays = Math.floor(totalHours / 24)
  const newHour = totalHours % 24

  const newGameTime: GameTime = {
    ...gameTime,
    gameDay: gameTime.gameDay + additionalDays,
    gameHour: newHour,
    gameMinute: newMinute,
    lastUpdateTimestamp: now,
    totalGameHours: gameTime.totalGameHours + gameHoursPassed,
  }

  return {
    realHoursPassed,
    gameHoursPassed,
    realMinutesPassed,
    newGameTime,
  }
}

/**
 * Get time scale based on difficulty and optional preset
 */
export const getTimeScaleForPreset = (preset: TimeScalePreset): number => {
  return TIME_SCALES[preset]
}

// Format game time for display
export const formatGameTime = (gameTime: GameTime): string => {
  const hour = gameTime.gameHour
  const minute = gameTime.gameMinute.toString().padStart(2, '0')
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `Day ${gameTime.gameDay} - ${displayHour}:${minute} ${ampm}`
}

// Format time remaining until deadline
export const formatTimeRemaining = (
  gameTime: GameTime,
  deadlineDay: number
): string => {
  const daysRemaining = deadlineDay - gameTime.gameDay
  const hoursRemaining = 24 - gameTime.gameHour

  if (daysRemaining < 0) {
    return 'DEADLINE PASSED'
  } else if (daysRemaining === 0) {
    return `${hoursRemaining} hours remaining`
  } else if (daysRemaining === 1) {
    return `1 day, ${hoursRemaining} hours remaining`
  } else {
    return `${daysRemaining} days remaining`
  }
}

// Get time of day description
export const getTimeOfDay = (hour: number): string => {
  if (hour < 6) return 'night'
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  if (hour < 21) return 'evening'
  return 'night'
}

// Get degen time description
export const getDegenTimeDescription = (gameTime: GameTime): string => {
  const hour = gameTime.gameHour
  const day = gameTime.gameDay

  // Special time-based descriptions
  const descriptions: Record<string, string[]> = {
    night: [
      'The degen hours. When fortunes are made and lost.',
      'Night shift at the charts. Sleep is for the weak.',
      'Late night trading. Your best and worst decisions happen now.',
      'The witching hour. Liquidity is thin and so is your sanity.',
    ],
    morning: [
      'GM. Time to check what the Asian markets did overnight.',
      'Rise and grind. The charts await.',
      'Morning coffee and green candles. The dream.',
      'Another day, another chance to make it.',
    ],
    afternoon: [
      'Lunch hour trading. The calm before the chaos.',
      'Midday. The bots are trading. Are you?',
      'Afternoon session. Europe winding down, US ramping up.',
      'Peak hours. Volume picking up. Stay sharp.',
    ],
    evening: [
      'Evening trading. The Americans are getting degen.',
      'Dinner can wait. The charts cannot.',
      'Prime time. This is when moves happen.',
      'Evening vibes. CT getting active.',
    ],
  }

  const timeOfDay = getTimeOfDay(hour)
  const options = descriptions[timeOfDay]
  if (!options || options.length === 0) {
    return 'Time to trade.'
  }
  const index = day % options.length
  return options[index] || options[0] || 'Time to trade.'
}

// Check if market is in "active" hours (more volatility)
export const isMarketActive = (hour: number): boolean => {
  // Crypto is 24/7 but certain hours are more active
  // US market hours: 9 AM - 4 PM EST (14:00 - 21:00 UTC)
  // Asian hours: 9 PM - 4 AM EST (02:00 - 09:00 UTC)
  // We'll simulate this with "active" periods
  return (hour >= 9 && hour <= 16) || (hour >= 21 || hour <= 4)
}

// Calculate time scale based on difficulty
export const getTimeScaleForDifficulty = (difficulty: string): number => {
  const scales: Record<string, number> = {
    paper_hands: 0.5, // Half speed - more time to think
    diamond_hands: 1.0, // Normal speed
    gigabrain: 2.0, // Double speed - pressure is on
  }
  return scales[difficulty] || 1.0
}
